/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\pages\Devis\ConfigurateurArticle\components\FinalisationModal.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
SELLERIE MARINE - COMPOSANT MODAL FINALISATION

📋 DESCRIPTION:
Composant React modal pour la finalisation complète des devis avec toutes les informations commerciales.
Interface exhaustive pour paramétrage client, conditions, livraison et génération PDF.

🎯 RESPONSABILITÉS:
- Gestion complète des données de finalisation (client, projet, conditions)
- Interface de sélection/création de clients avec formulaire intégré
- Configuration des conditions de paiement avec calcul automatique d'acompte
- Gestion des délais et dates avec calculs automatiques
- Configuration de la livraison avec adresses multiples
- Calcul automatique des frais de déplacement avec géolocalisation
- Sauvegarde persistante des données de finalisation dans le devis

🔗 DÉPENDANCES:
- EntrepriseService: Données atelier pour calcul distances
- ClientService: Gestion clients (lecture, création)
- DevisApiService: Sauvegarde données finalisation
- React: Hooks useState, useEffect pour gestion d'état complexe

📡 INTERACTIONS:
- Parent: Réception données existantes et callbacks onClose/onSave
- Services: Chargement clients, création nouveaux clients, sauvegarde devis
- APIs externes: Géolocalisation pour calcul automatique distances
- Interface: Validation formulaire avec champs requis

📊 STATUT: ACTIF (Production)
📅 Dernière mise à jour: Juin 2025
👤 Responsable: Équipe développement

💡 NOTES:
- Calculs automatiques: acompte (% conditions), dates (délais), distances (géoloc)
- Interface adaptative: création client intégrée, adresses multiples
- Validation complète avec champs obligatoires marqués
- Sauvegarde directe dans devis pour persistance
- Support géolocalisation avec fallback calcul à vol d'oiseau
- Interface responsive plein écran avec sections organisées
*/
import React, { useState, useEffect } from 'react';
import { entrepriseService } from '../../../../services/EntrepriseService';
import { clientService } from '../../../../services/ClientService';
import { devisApiService } from '../../../../services/DevisApiService';

export interface FinalisationData {
  // Client & Projet
  clientId?: string;
  nomProjet?: string;
  dateDevis?: string;
  
  // Données client enrichies (pour PDF)
  clientNom?: string;
  clientSociete?: string;
  clientTelephone?: string;
  clientEmail?: string;
  adresseClient?: string;
  
  // Conditions de paiement
  conditionsPaiement?: string;
  montantAcompte?: number;
  modalitesPaiement?: string[];
  
  // Délais & Validité
  delaisLivraison?: string;
  dateValiditeDevis?: string;
  
  // Mentions libres
  mentionsLibres?: string;
  
  // Signature client
  signatureConfig?: {
    nomSignataire?: string;
    fonction?: string;
    entreprise?: string;
  };
  inclureBonPourAccord?: boolean;
  
  // Livraison
  adresseLivraison?: string;
  adresseDifferente?: boolean;
  adresseChantier?: string;
  notesLivraison?: string;
  dateLivraison?: string;
  
  // Frais de déplacement
  fraisDeplacementConfig?: {
    distance?: number;
    franchiseKm?: number;
    tarifKm?: number;
    montantCalcule?: number;
    allerRetour?: boolean;
  };
  
  // Petites fournitures
  petitesFournitures?: {
    inclure?: boolean;
    taux?: number;
    montantCalcule?: number;
  };

  // Frais de port
  fraisPort?: {
    statut?: 'INCONNU' | 'ESTIME' | 'CONFIRME';
    montant?: number;
  };
}

interface FinalisationModalProps {
  isOpen: boolean;
  finalisationData: FinalisationData;
  clientInfo?: any;
  devisExistant?: any;
  montantTotal: number;
  onClose: () => void;
  onSave: (data: FinalisationData) => void;
  soumisATVA?: boolean;
  tauxTVA?: number;
  viewMode?: 'full' | 'fees';
}

const FinalisationModal: React.FC<FinalisationModalProps> = ({
  isOpen,
  finalisationData,
  clientInfo,
  devisExistant,
  montantTotal,
  onClose,
  onSave,
  soumisATVA,
  tauxTVA,
  viewMode = 'full'
}) => {
  const [formData, setFormData] = useState<FinalisationData>(finalisationData);
  const [calculatingDistance, setCalculatingDistance] = useState(false);
  
  // États pour la gestion des clients
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>(clientInfo?.id || '');
  const [selectedClient, setSelectedClient] = useState<any>(clientInfo);
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [newClient, setNewClient] = useState({
    nom: '',
    prenom: '',
    entreprise: '',
    email: '',
    telephone: '',
    adresse: '',
    codePostal: '',
    ville: '',
    pays: 'France'
  });

  // Charger la liste des clients
  useEffect(() => {
    const fetchClients = async () => {
      if (isOpen) {
        try {
          const clientsData = await clientService.getClients();
          
          // ✅ CORRECTION: Dédupliquer les clients par ID
          const clientsUniques = clientsData.reduce((acc: any[], client: any) => {
            if (!acc.find(c => c.id === client.id)) {
              acc.push(client);
            }
            return acc;
          }, []);
          
          setClients(clientsUniques);
          console.log('✅ Clients chargés (dédupliqués):', clientsUniques.length);
          
          // Si on a un client existant, s'assurer qu'il est sélectionné par défaut
          if (clientInfo?.id) {
            setSelectedClientId(clientInfo.id);
            setSelectedClient(clientInfo);
          }
        } catch (error) {
          console.error('Erreur lors du chargement des clients:', error);
        }
      }
    };
    fetchClients();
  }, [isOpen]);

  // Initialisation intelligente à l'ouverture ET quand finalisationData change
  useEffect(() => {
    if (isOpen) {

      // ✅ CORRECTION: Vérifier la présence de données avec une condition plus robuste
      const hasExistingData = finalisationData && (
        finalisationData.nomProjet ||
        finalisationData.conditionsPaiement ||
        finalisationData.delaisLivraison ||
        finalisationData.mentionsLibres ||
        finalisationData.dateValiditeDevis ||
        (finalisationData.modalitesPaiement && finalisationData.modalitesPaiement.length > 0) ||
        finalisationData.petitesFournitures?.inclure ||
        finalisationData.signatureConfig?.nomSignataire ||
        finalisationData.adresseLivraison ||
        finalisationData.fraisDeplacementConfig?.distance
      );

      if (hasExistingData) {

        // ✅ CORRECTION: Recalculer les petites fournitures si elles sont configurées
        let dataCorrigees = { ...finalisationData };
        if (finalisationData.petitesFournitures?.inclure && finalisationData.petitesFournitures?.taux) {
          const taux = finalisationData.petitesFournitures.taux;
          const montantCorrige = (montantTotal * taux) / 100;

            dataCorrigees = {
            ...finalisationData,
            petitesFournitures: {
              ...finalisationData.petitesFournitures,
              montantCalcule: montantCorrige
            }
          };
        }

        // Normaliser les frais de port avec un statut par défaut
        if (!dataCorrigees.fraisPort) {
          dataCorrigees = {
            ...dataCorrigees,
            fraisPort: { statut: 'INCONNU', montant: undefined }
          };
        } else if (!dataCorrigees.fraisPort.statut) {
          dataCorrigees = {
            ...dataCorrigees,
            fraisPort: { ...dataCorrigees.fraisPort, statut: 'INCONNU' }
          };
        }
        
        // ✅ CORRECTION: Mettre à jour l'état du client sélectionné si présent
        if (finalisationData.clientId && clients.length > 0) {
          const client = clients.find(c => c.id === finalisationData.clientId);
          if (client) {
            setSelectedClientId(finalisationData.clientId);
            setSelectedClient(client);

          }
        }

        setFormData(dataCorrigees);
      } else {

        // Première initialisation avec valeurs par défaut
        const today = new Date().toISOString().split('T')[0];
        const validityDate = new Date();
        validityDate.setDate(validityDate.getDate() + 30);
        
        const defaultFraisConfig = {
          franchiseKm: 50,
          tarifKm: 0.5,
          distance: undefined,
          montantCalcule: undefined
        };

        // Pré-remplir avec les données du client si disponibles
        let adresseLivraison = '';
        let signatureConfig = {};
        
        if (clientInfo) {
          // Construire l'adresse du client
          adresseLivraison = [clientInfo.adresse, clientInfo.codePostal, clientInfo.ville]
            .filter(Boolean)
            .join('\n');
          
          // Pré-remplir les données de signature
          signatureConfig = {
            nomSignataire: `${clientInfo.nom} ${clientInfo.prenom}`,
            entreprise: clientInfo.societe || ''
          };
        }

        const initialData: FinalisationData = {
          clientId: clientInfo?.id,
          nomProjet: devisExistant?.projet,
          dateDevis: devisExistant?.date?.split('T')[0] || today,
          dateValiditeDevis: validityDate.toISOString().split('T')[0],
          inclureBonPourAccord: true,
          fraisDeplacementConfig: defaultFraisConfig,
          fraisPort: { statut: 'INCONNU', montant: undefined },
          signatureConfig: signatureConfig,
          adresseLivraison: adresseLivraison
        };

        setFormData(initialData);
      }
    }
  }, [isOpen, finalisationData, clientInfo, devisExistant, montantTotal]);

  // Calculer l'acompte automatiquement
  useEffect(() => {
    if (formData.conditionsPaiement && formData.conditionsPaiement.trim() !== '') {
      // Formats reconnus: "30%", "30 %", "30% à la commande", "Acompte de 50%", etc.
      const match = formData.conditionsPaiement.match(/(\d+)\s*%/i);
      if (match) {
        const pourcentage = parseInt(match[1]);
        const montantCalcule = (montantTotal * pourcentage) / 100;
        
        setFormData(prev => ({
          ...prev,
          montantAcompte: montantCalcule
        }));
      } else {
      }
    }
  }, [formData.conditionsPaiement, montantTotal]);

  // Calculer les frais de déplacement
  useEffect(() => {
    const config = formData.fraisDeplacementConfig;
    if (config?.distance && config?.franchiseKm !== undefined && config?.tarifKm) {
      // Si facturer retour, doubler la distance AVANT de soustraire la franchise
      const distanceTotale = config.allerRetour ? config.distance * 2 : config.distance;
      // Appliquer la franchise une seule fois sur la distance totale
      const kmFacturables = Math.max(0, distanceTotale - config.franchiseKm);
      const montantCalcule = kmFacturables * config.tarifKm;
      setFormData(prev => ({
        ...prev,
        fraisDeplacementConfig: {
          ...(prev.fraisDeplacementConfig || {}),
          montantCalcule
        }
      }));
    }
  }, [formData.fraisDeplacementConfig?.distance, formData.fraisDeplacementConfig?.franchiseKm, formData.fraisDeplacementConfig?.tarifKm, formData.fraisDeplacementConfig?.allerRetour]);

  // Calculer la date de livraison automatiquement basée sur le délai
  useEffect(() => {
    if (formData.delaisLivraison && formData.delaisLivraison.trim() !== '') {
      // Extraire le nombre de semaines ou de jours du délai
      // Formats acceptés: "3 semaines", "2 semaine", "14 jours", "1 jour", "21j", "3s", "30 j"
      let jours = 0;
      
      // Recherche de semaines
      const matchSemaines = formData.delaisLivraison.match(/(\d+)\s*(?:semaines?|s)\b/i);
      if (matchSemaines) {
        jours = parseInt(matchSemaines[1]) * 7;
      } else {
        // Recherche de jours - formats: "14 jours", "21j", "30 j"
        const matchJours = formData.delaisLivraison.match(/(\d+)\s*(?:jours?|j)\b/i);
        if (matchJours) {
          jours = parseInt(matchJours[1]);
        }
      }
      
      if (jours > 0) {
        const dateLivraison = new Date();
        dateLivraison.setDate(dateLivraison.getDate() + jours);
        
        const dateFormatee = dateLivraison.toISOString().split('T')[0];
        
        setFormData(prev => ({
          ...prev,
          dateLivraison: dateFormatee
        }));
      }
    }
  }, [formData.delaisLivraison]);

  const handleInputChange = (field: string, value: any, section?: string) => {
    if (section) {
      setFormData(prev => {
        const currentSection = prev[section as keyof FinalisationData] as any;
        return {
          ...prev,
          [section]: {
            ...(currentSection || {}),
            [field]: value
          }
        };
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Fonction pour calculer la distance automatiquement
  const calculerDistance = async () => {
    setCalculatingDistance(true);
    
    try {
      // Récupérer l'adresse de l'entreprise depuis le service
      const entrepriseData = await entrepriseService.getEntreprise();
      const adresseAtelier = [
        entrepriseData.adresse,
        entrepriseData.codePostal,
        entrepriseData.ville
      ].filter(Boolean).join(' ');
      
      if (!adresseAtelier || !adresseAtelier.trim()) {
        alert('Veuillez configurer l\'adresse de votre entreprise dans les paramètres avant de calculer la distance.');
        return;
      }
      
      // Adresse de destination
      let adresseDestination = formData.adresseDifferente 
        ? formData.adresseChantier 
        : formData.adresseLivraison;
      
      if (!adresseDestination || !adresseDestination.trim()) {
        alert('Veuillez saisir une adresse de destination pour calculer la distance.');
        return;
      }
      
      // Nettoyer l'adresse : remplacer les retours à la ligne par des espaces
      adresseDestination = adresseDestination.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
      
      // Géocoder l'adresse de l'atelier
      const responseAtelier = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(adresseAtelier)}&limit=1`);
      const dataAtelier = await responseAtelier.json();
      
      if (!dataAtelier || dataAtelier.length === 0) {
        // Essayer avec une adresse simplifiée (ville + code postal seulement)
        const adresseSimplifiee = `${entrepriseData.codePostal} ${entrepriseData.ville}`;
        
        const responseSimple = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(adresseSimplifiee)}&limit=1`);
        const dataSimple = await responseSimple.json();
        
        if (!dataSimple || dataSimple.length === 0) {
          throw new Error(`Impossible de localiser l'adresse de l'atelier. Essayé: "${adresseAtelier}" et "${adresseSimplifiee}"`);
        }
        
        // Utiliser l'adresse simplifiée
        dataAtelier[0] = dataSimple[0];
      }
      
      // Géocoder l'adresse de destination
      const responseDestination = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(adresseDestination)}&limit=1`);
      const dataDestination = await responseDestination.json();
      
      if (!dataDestination || dataDestination.length === 0) {
        // Essayer avec une adresse simplifiée (extraire ville et code postal)
        const lignes = adresseDestination.split('\n');
        let adresseSimplifieeDestination = '';
        
        // Chercher une ligne qui ressemble à "code postal + ville"
        for (const ligne of lignes) {
          const match = ligne.match(/(\d{5})\s+(.+)/);
          if (match) {
            adresseSimplifieeDestination = `${match[1]} ${match[2]}`;
            break;
          }
        }
        
        if (adresseSimplifieeDestination) {
          const responseSimpleDestination = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(adresseSimplifieeDestination)}&limit=1`);
          const dataSimpleDestination = await responseSimpleDestination.json();
          
          if (!dataSimpleDestination || dataSimpleDestination.length === 0) {
            throw new Error(`Impossible de localiser l'adresse de destination. Essayé: "${adresseDestination}" et "${adresseSimplifieeDestination}"`);
          }
          
          // Utiliser l'adresse simplifiée
          dataDestination[0] = dataSimpleDestination[0];
        } else {
          throw new Error(`Impossible de localiser l'adresse de destination: "${adresseDestination}"`);
        }
      }
      
      // Calculer la vraie distance routière avec OpenRouteService (gratuit)
      const lat1 = parseFloat(dataAtelier[0].lat);
      const lon1 = parseFloat(dataAtelier[0].lon);
      const lat2 = parseFloat(dataDestination[0].lat);
      const lon2 = parseFloat(dataDestination[0].lon);
      
      // Utiliser l'API OpenRouteService pour le calcul de distance routière
      // API avec clé API - 2000 requêtes/jour gratuites
      const apiKey = process.env.REACT_APP_OPENROUTESERVICE_API_KEY;
      const routingUrl = `https://api.openrouteservice.org/v2/directions/driving-car?api_key=${apiKey}&start=${lon1},${lat1}&end=${lon2},${lat2}`;
      
      let distanceKm = 0;
      
      try {
        const routingResponse = await fetch(routingUrl);
        const routingData = await routingResponse.json();
        
        if (routingData.features && routingData.features.length > 0) {
          // Distance en mètres, convertir en km
          const distanceMetres = routingData.features[0].properties.segments[0].distance;
          distanceKm = Math.round(distanceMetres / 1000);
        } else {
          throw new Error('Pas de route trouvée par OpenRouteService');
        }
      } catch (routingError) {
        console.warn('⚠️ Erreur API routing, fallback sur calcul à vol d\'oiseau:', routingError);
        
        // Fallback : calcul à vol d'oiseau avec coefficient plus conservateur
        const R = 6371; // Rayon de la Terre en km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                  Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                  Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        const distanceVolOiseau = R * c;
        
        // Coefficient plus conservateur pour les longues distances
        let coefficient = 1.2; // 20% par défaut
        if (distanceVolOiseau > 100) coefficient = 1.15; // 15% pour > 100km
        if (distanceVolOiseau > 300) coefficient = 1.1;  // 10% pour > 300km
        
        distanceKm = Math.round(distanceVolOiseau * coefficient);
        
        // Avertir l'utilisateur que c'est une estimation
        alert(`⚠️ Distance calculée par estimation (${distanceKm} km)\n\nL'API de routage n'est pas disponible. La distance est estimée à vol d'oiseau avec un coefficient de sécurité.\n\nVous pouvez ajuster manuellement si nécessaire.`);
      }
      
      // Mettre à jour la distance dans le formulaire
      handleInputChange('distance', distanceKm, 'fraisDeplacementConfig');
      
    } catch (error) {
      console.error('Erreur calcul distance:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      console.error('Détails de l\'erreur:', errorMessage);
      alert(`Erreur: ${errorMessage}\n\nVeuillez saisir la distance manuellement.`);
    } finally {
      setCalculatingDistance(false);
    }
  };

  const handleSave = async () => {
    
    try {
      // Enrichir les données de finalisation avec les informations du client sélectionné
      const finalisationDataEnrichie = { ...formData } as any;
      
      if (selectedClient) {
        finalisationDataEnrichie.clientNom = `${selectedClient.prenom} ${selectedClient.nom}`.trim();
        finalisationDataEnrichie.clientSociete = selectedClient.societe || '';
        finalisationDataEnrichie.clientTelephone = selectedClient.telephone || '';
        finalisationDataEnrichie.clientEmail = selectedClient.email || '';
        
        // Construire l'adresse complète du client
        const adresseClientParts = [
          selectedClient.adresse,
          `${selectedClient.codePostal} ${selectedClient.ville}`.trim()
        ].filter(Boolean);
        finalisationDataEnrichie.adresseClient = adresseClientParts.join('\n');
      }
      
      // Sauvegarder directement dans le devis
      if (devisExistant) {
        const devisAMettreAJour = {
          ...devisExistant,
          finalisationData: finalisationDataEnrichie
        };
        
        await devisApiService.saveDevis(devisAMettreAJour);
        
        // Notifier le parent que la sauvegarde est terminée (pour recharger les données)
        onSave(finalisationDataEnrichie);
      }
      
      onClose();
    } catch (error) {
      console.error('❌ [FinalisationModal] Erreur lors de la sauvegarde:', error);
      alert('Erreur lors de la sauvegarde des données de finalisation');
    }
  };

  const isFeesMode = viewMode === 'fees';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-4xl h-full max-h-none flex flex-col">
        {/* En-tête */}
        <div className="flex items-center justify-between p-6 border-b bg-gray-50 rounded-t-lg">
          <h3 className="text-2xl font-semibold text-gray-900">
            Finalisation du devis
          </h3>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200 p-2 hover:bg-gray-200 rounded-full"
            onClick={onClose}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Contenu */}
        <div className="flex-1 overflow-auto p-6">
          <div className="space-y-8">
            
            {!isFeesMode && (
            <>
            {/* 1. Client & Projet */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-lg font-medium mb-4 flex items-center">
                🧾 Client & Projet
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client <span className="text-red-500">*</span>
                  </label>
                  {!showNewClientForm ? (
                    <div className="flex space-x-2">
                      <select
                        value={selectedClientId}
                        onChange={(e) => {
                          const clientId = e.target.value;
                          setSelectedClientId(clientId);
                          if (clientId === 'new') {
                            setShowNewClientForm(true);
                          } else {
                            const client = clients.find(c => c.id === clientId);
                            setSelectedClient(client);
                            handleInputChange('clientId', clientId);
                            
                            // Mettre à jour automatiquement l'adresse de livraison avec l'adresse du client
                            if (client) {
                              const adresseClient = [client.adresse, client.codePostal, client.ville].filter(Boolean).join('\n');
                              handleInputChange('adresseLivraison', adresseClient);
                              
                              // Mettre à jour aussi les données de signature
                              handleInputChange('nomSignataire', `${client.nom} ${client.prenom}`, 'signatureConfig');
                              handleInputChange('entreprise', client.societe || '', 'signatureConfig');
                            }
                          }
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Sélectionner un client</option>
                        {clients.map(client => (
                          <option key={client.id} value={client.id}>
                            {client.societe || `${client.prenom} ${client.nom}`}
                          </option>
                        ))}
                        <option value="new">+ Créer un nouveau client</option>
                      </select>
                    </div>
                  ) : (
                    <div className="space-y-3 p-4 border border-gray-300 rounded-md bg-blue-50">
                      <div className="flex justify-between items-center">
                        <h5 className="font-medium text-gray-900">Nouveau client</h5>
                        <button
                          type="button"
                          onClick={() => {
                            setShowNewClientForm(false);
                            setSelectedClientId(clientInfo?.id || '');
                          }}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          ✕
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          placeholder="Nom *"
                          value={newClient.nom}
                          onChange={(e) => setNewClient(prev => ({ ...prev, nom: e.target.value }))}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <input
                          type="text"
                          placeholder="Prénom *"
                          value={newClient.prenom}
                          onChange={(e) => setNewClient(prev => ({ ...prev, prenom: e.target.value }))}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <input
                          type="text"
                          placeholder="Entreprise"
                          value={newClient.entreprise}
                          onChange={(e) => setNewClient(prev => ({ ...prev, entreprise: e.target.value }))}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <input
                          type="email"
                          placeholder="Email"
                          value={newClient.email}
                          onChange={(e) => setNewClient(prev => ({ ...prev, email: e.target.value }))}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <input
                          type="tel"
                          placeholder="Téléphone"
                          value={newClient.telephone}
                          onChange={(e) => setNewClient(prev => ({ ...prev, telephone: e.target.value }))}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <input
                          type="text"
                          placeholder="Adresse"
                          value={newClient.adresse}
                          onChange={(e) => setNewClient(prev => ({ ...prev, adresse: e.target.value }))}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <input
                          type="text"
                          placeholder="Code postal"
                          value={newClient.codePostal}
                          onChange={(e) => setNewClient(prev => ({ ...prev, codePostal: e.target.value }))}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <input
                          type="text"
                          placeholder="Ville"
                          value={newClient.ville}
                          onChange={(e) => setNewClient(prev => ({ ...prev, ville: e.target.value }))}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!newClient.nom || !newClient.prenom) {
                            alert('Nom et prénom sont obligatoires');
                            return;
                          }
                          try {
                            const clientCree = await clientService.saveClient(newClient);
                            setClients(prev => [...prev, clientCree]);
                            setSelectedClientId(clientCree.id || '');
                            setSelectedClient(clientCree);
                            handleInputChange('clientId', clientCree.id);
                            
                            // Mettre à jour automatiquement l'adresse de livraison avec l'adresse du nouveau client
                            const adresseClient = [clientCree.adresse, clientCree.codePostal, clientCree.ville].filter(Boolean).join('\n');
                            handleInputChange('adresseLivraison', adresseClient);
                            
                            // Mettre à jour aussi les données de signature
                            handleInputChange('nomSignataire', `${clientCree.nom} ${clientCree.prenom}`, 'signatureConfig');
                            handleInputChange('entreprise', clientCree.societe || '', 'signatureConfig');
                            
                            setShowNewClientForm(false);
                            // Réinitialiser le formulaire
                            setNewClient({
                              nom: '', prenom: '', entreprise: '', email: '', telephone: '', adresse: '', codePostal: '', ville: '', pays: 'France'
                            });
                          } catch (error) {
                            console.error('Erreur création client:', error);
                            alert('Erreur lors de la création du client');
                          }
                        }}
                        className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors duration-200"
                      >
                        Créer le client
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom du projet <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.nomProjet || ''}
                    onChange={(e) => handleInputChange('nomProjet', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Ex: Coussins bateau Jeanneau"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date du devis <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.dateDevis || ''}
                    onChange={(e) => handleInputChange('dateDevis', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>
            </>
            )}

            {!isFeesMode && (
            <>
            {/* 2. Conditions de paiement */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-lg font-medium mb-4 flex items-center">
                💶 Conditions de paiement
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Conditions de paiement <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.conditionsPaiement || ''}
                    onChange={(e) => handleInputChange('conditionsPaiement', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Ex: 30% à la commande, 50 %, Acompte de 40%"
                  />
                  <div className="mt-1 text-xs text-gray-500">
                    💡 Formats reconnus pour calcul automatique de l'acompte : "30%", "50 %", "Acompte de 40%"
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Montant acompte (auto)
                  </label>
                  <input
                    type="number"
                    value={formData.montantAcompte || ''}
                    onChange={(e) => handleInputChange('montantAcompte', parseFloat(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Calculé automatiquement"
                    step="0.01"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Modalités de paiement acceptées
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {['Virement', 'Chèque', 'Carte bancaire', 'Espèces'].map((modalite) => (
                    <label key={modalite} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.modalitesPaiement?.includes(modalite) || false}
                        onChange={(e) => {
                          const currentModalites = formData.modalitesPaiement || [];
                          if (e.target.checked) {
                            handleInputChange('modalitesPaiement', [...currentModalites, modalite]);
                          } else {
                            handleInputChange('modalitesPaiement', currentModalites.filter(m => m !== modalite));
                          }
                        }}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{modalite}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            </>
            )}

            {/* 3. Petites fournitures */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-lg font-medium mb-4 flex items-center">
                🔧 Petites fournitures
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.petitesFournitures?.inclure || false}
                      onChange={(e) => {
                        const inclure = e.target.checked;
                        handleInputChange('inclure', inclure, 'petitesFournitures');
                        
                        // Si on coche, initialiser avec les valeurs par défaut
                        if (inclure) {
                          handleInputChange('taux', 5, 'petitesFournitures');
                          // 🔍 DEBUG: Vérifier le montant utilisé pour le calcul
                          
                          // Calculer le montant automatiquement sur le montant HT final (après remises et économies)
                          const montantCalcule = (montantTotal * 5) / 100;
                          handleInputChange('montantCalcule', montantCalcule, 'petitesFournitures');
                        } else {
                          // Si on décoche, remettre à zéro
                          handleInputChange('montantCalcule', 0, 'petitesFournitures');
                        }
                      }}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      Inclure les petites fournitures
                    </span>
                  </label>
                  <div className="mt-1 text-xs text-gray-500">
                    💡 Fil, boutons, élastiques, etc.
                  </div>
                </div>
                
                {formData.petitesFournitures?.inclure && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Taux (%)
                      </label>
                      <input
                        type="number"
                        value={formData.petitesFournitures?.taux || 5}
                        onChange={(e) => {
                          const taux = parseFloat(e.target.value) || 0;
                          handleInputChange('taux', taux, 'petitesFournitures');
                          // 🔍 DEBUG: Vérifier le montant utilisé pour le calcul lors du changement de taux
                          
                          // Recalculer le montant automatiquement
                          const montantCalcule = (montantTotal * taux) / 100;
                          handleInputChange('montantCalcule', montantCalcule, 'petitesFournitures');
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="5"
                        step="0.1"
                        min="0"
                        max="100"
                      />
                      <div className="mt-1 text-xs text-gray-500">
                        Appliqué sur le total HT
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Montant calculé (€)
                      </label>
                      <input
                        type="number"
                        value={formData.petitesFournitures?.montantCalcule?.toFixed(2) || '0.00'}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                        step="0.01"
                      />
                      <div className="mt-1 text-xs text-gray-500">
                        {formData.petitesFournitures?.taux || 0}% de {montantTotal.toFixed(2)}€
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {!isFeesMode && (
            <>
            {/* 4. Délais & Validité */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-lg font-medium mb-4 flex items-center">
                ⏱ Délais & Validité
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Délais de livraison <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.delaisLivraison || ''}
                    onChange={(e) => handleInputChange('delaisLivraison', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Ex: 3 semaines, 14 jours, 21j, 2s"
                  />
                  <div className="mt-1 text-xs text-gray-500">
                    💡 Formats reconnus pour calcul automatique de la date : "3 semaines", "14 jours", "21j", "2s", "30 j"
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de validité du devis
                  </label>
                  <input
                    type="date"
                    value={formData.dateValiditeDevis || ''}
                    onChange={(e) => handleInputChange('dateValiditeDevis', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>
            </>
            )}

            {!isFeesMode && (
            <>
            {/* 4. Mentions libres */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-lg font-medium mb-4 flex items-center">
                📝 Mentions libres (commerciales)
              </h4>
              <textarea
                value={formData.mentionsLibres || ''}
                onChange={(e) => handleInputChange('mentionsLibres', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Ex: Garantie 2 ans, Installation comprise, Matériaux certifiés marine..."
              />
            </div>
            </>
            )}

            {!isFeesMode && (
            <>
            {/* 5. Signature client */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-lg font-medium mb-4 flex items-center">
                ✍️ Bloc signature client
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom du signataire <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.signatureConfig?.nomSignataire || ''}
                    onChange={(e) => handleInputChange('nomSignataire', e.target.value, 'signatureConfig')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Ex: Jean Dupont"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fonction
                  </label>
                  <input
                    type="text"
                    value={formData.signatureConfig?.fonction || ''}
                    onChange={(e) => handleInputChange('fonction', e.target.value, 'signatureConfig')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Ex: Gérant"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Entreprise
                  </label>
                  <input
                    type="text"
                    value={formData.signatureConfig?.entreprise || ''}
                    onChange={(e) => handleInputChange('entreprise', e.target.value, 'signatureConfig')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Ex: Nautica SARL"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.inclureBonPourAccord || false}
                    onChange={(e) => handleInputChange('inclureBonPourAccord', e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Inclure un bon pour accord <span className="text-red-500">*</span>
                  </span>
                </label>
              </div>
            </div>
            </>
            )}

            {/* 6. Livraison */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-lg font-medium mb-4 flex items-center">
                🚚 Informations de livraison (facultatif)
              </h4>
              
              {/* Case à cocher pour adresse différente */}
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.adresseDifferente || false}
                    onChange={(e) => handleInputChange('adresseDifferente', e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    L'adresse du chantier est différente de l'adresse du client
                  </span>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {formData.adresseDifferente ? 'Adresse du client' : 'Adresse de livraison'}
                  </label>
                  <textarea
                    value={formData.adresseLivraison || ''}
                    onChange={(e) => handleInputChange('adresseLivraison', e.target.value)}
                    rows={2}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      formData.adresseDifferente ? 'bg-gray-100' : ''
                    }`}
                    placeholder="Ex: Port de Toulon – Quai 12"
                    disabled={formData.adresseDifferente}
                  />
                </div>
                
                {formData.adresseDifferente && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Adresse du chantier <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.adresseChantier || ''}
                      onChange={(e) => handleInputChange('adresseChantier', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Ex: Chantier naval de La Ciotat – Bassin 3"
                    />
                  </div>
                )}
                
                <div className={formData.adresseDifferente ? 'md:col-start-1' : ''}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes de livraison
                  </label>
                  <textarea
                    value={formData.notesLivraison || ''}
                    onChange={(e) => handleInputChange('notesLivraison', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Ex: Prévenir 24h avant livraison"
                  />
                </div>
              </div>
              
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date de livraison souhaitée
                </label>
                <input
                  type="date"
                  value={formData.dateLivraison || ''}
                  onChange={(e) => handleInputChange('dateLivraison', e.target.value)}
                  className="w-full md:w-1/3 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            {/* 7. Frais de déplacement */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-lg font-medium mb-4 flex items-center">
                🚚 Frais de déplacement avec franchise km
              </h4>
              
              {/* Case à cocher Facturer le retour */}
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.fraisDeplacementConfig?.allerRetour || false}
                    onChange={(e) => handleInputChange('allerRetour', e.target.checked, 'fraisDeplacementConfig')}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Facturer le retour ? (Aller/Retour)
                  </span>
                </label>
                <div className="mt-1 text-xs text-gray-500 ml-6">
                  💡 La distance sera doublée pour l'aller-retour. La franchise KM ne s'applique qu'une seule fois.
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Distance (km) {formData.fraisDeplacementConfig?.allerRetour && <span className="text-blue-600">(Aller simple)</span>}
                  </label>
                  <input
                    type="number"
                    value={formData.fraisDeplacementConfig?.distance ?? ''}
                    onChange={(e) => handleInputChange('distance', parseFloat(e.target.value), 'fraisDeplacementConfig')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Ex: 120"
                    step="0.1"
                  />
                  <button
                    type="button"
                    onClick={calculerDistance}
                    disabled={calculatingDistance || (!formData.adresseLivraison && !formData.adresseChantier)}
                    className="mt-2 w-full px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200 text-sm"
                    title="Calculer automatiquement la distance depuis l'atelier"
                  >
                    {calculatingDistance ? 'Calcul en cours...' : 'Calculer automatiquement la distance'}
                  </button>
                  <div className="mt-1 text-xs text-gray-500">
                    💡 Calcul automatique depuis l'adresse de l'atelier
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Franchise gratuite (km)
                  </label>
                  <input
                    type="number"
                    value={formData.fraisDeplacementConfig?.franchiseKm ?? ''}
                    onChange={(e) => handleInputChange('franchiseKm', parseFloat(e.target.value), 'fraisDeplacementConfig')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Ex: 50"
                    step="0.1"
                  />
                  <div className="mt-1 text-xs text-gray-500">
                    Indiquez 0 pour facturer dès le premier kilomètre (sans franchise).
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tarif au km (€)
                  </label>
                  <input
                    type="number"
                    value={formData.fraisDeplacementConfig?.tarifKm ?? ''}
                    onChange={(e) => handleInputChange('tarifKm', parseFloat(e.target.value), 'fraisDeplacementConfig')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Ex: 0.50"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Montant calculé (€)
                  </label>
                  <input
                    type="number"
                    value={formData.fraisDeplacementConfig?.montantCalcule || 0}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                    step="0.01"
                  />
                </div>
              </div>
              {formData.fraisDeplacementConfig?.distance && formData.fraisDeplacementConfig?.franchiseKm !== undefined && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="text-sm font-medium text-gray-700 mb-1">Détail du calcul :</div>
                  {formData.fraisDeplacementConfig?.allerRetour ? (
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>• Distance aller-retour : {formData.fraisDeplacementConfig.distance} km × 2 = <strong>{formData.fraisDeplacementConfig.distance * 2} km</strong></div>
                      <div>• Franchise (appliquée une seule fois) : {formData.fraisDeplacementConfig.franchiseKm} km</div>
                      <div>• Km facturables : {formData.fraisDeplacementConfig.distance * 2} - {formData.fraisDeplacementConfig.franchiseKm} = <strong>{Math.max(0, formData.fraisDeplacementConfig.distance * 2 - formData.fraisDeplacementConfig.franchiseKm)} km</strong></div>
                      <div>• Montant : {Math.max(0, formData.fraisDeplacementConfig.distance * 2 - formData.fraisDeplacementConfig.franchiseKm)} km × {formData.fraisDeplacementConfig.tarifKm || 0}€/km = <strong>{formData.fraisDeplacementConfig.montantCalcule?.toFixed(2) || 0}€</strong></div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>• Distance aller simple : <strong>{formData.fraisDeplacementConfig.distance} km</strong></div>
                      <div>• Franchise : {formData.fraisDeplacementConfig.franchiseKm} km</div>
                      <div>• Km facturables : {formData.fraisDeplacementConfig.distance} - {formData.fraisDeplacementConfig.franchiseKm} = <strong>{Math.max(0, formData.fraisDeplacementConfig.distance - formData.fraisDeplacementConfig.franchiseKm)} km</strong></div>
                      <div>• Montant : {Math.max(0, formData.fraisDeplacementConfig.distance - formData.fraisDeplacementConfig.franchiseKm)} km × {formData.fraisDeplacementConfig.tarifKm || 0}€/km = <strong>{formData.fraisDeplacementConfig.montantCalcule?.toFixed(2) || 0}€</strong></div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* 8. Frais de port (global) */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-lg font-medium mb-4 flex items-center">
                🚚 Frais de port (global)
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Statut des frais de port
                  </label>
                  <select
                    value={formData.fraisPort?.statut || 'INCONNU'}
                    onChange={(e) => handleInputChange('statut', e.target.value as 'INCONNU' | 'ESTIME' | 'CONFIRME', 'fraisPort')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="INCONNU">Inconnu / non inclus</option>
                    <option value="ESTIME">Estimé (inclus)</option>
                    <option value="CONFIRME">Confirmé (inclus)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {soumisATVA === false ? 'Montant FP (TTC)' : 'Montant FP (HT)'}
                  </label>
                  <input
                    type="number"
                    value={formData.fraisPort?.montant ?? ''}
                    onChange={(e) => handleInputChange('montant', parseFloat(e.target.value || '0'), 'fraisPort')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Ex: 25"
                    step="0.01"
                    min="0"
                  />
                  <div className="mt-1 text-xs text-gray-500">
                    Saisie {soumisATVA === false ? 'TTC' : 'HT'} (statut TVA actuel {soumisATVA === false ? 'non soumis' : 'soumis'}).
                  </div>
                </div>

                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rappel
                  </label>
                  <div className="text-sm text-gray-600 bg-white border border-gray-200 rounded-md p-3">
                    Si plusieurs fournisseurs, additionnez leurs frais de port ici (l’application ne passe pas commande à votre place).
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Pied de modal */}
        <div className="flex justify-between items-center p-6 border-t bg-gray-50 rounded-b-lg">
          <button
            type="button"
            className="px-6 py-3 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors duration-200 font-medium"
            onClick={onClose}
          >
            Annuler
          </button>
          <button
            type="button"
            className="px-6 py-3 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors duration-200 font-medium"
            onClick={handleSave}
          >
            Enregistrer les informations
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinalisationModal;
